import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const TYPES = [["text", "نص"], ["number", "رقم"], ["date", "تاريخ"], ["expiry", "تاريخ انتهاء (عدّاد)"]];
const uid = () => Math.random().toString(36).slice(2, 10);

export const ExpiryBadge = ({ date }) => {
  if (!date) return null;
  const days = Math.ceil((new Date(date) - new Date(new Date().toISOString().slice(0, 10))) / 86400000);
  if (Number.isNaN(days)) return null;
  const cls = days < 0 ? "bg-red-100 text-red-700" : days <= 30 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
  return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`} data-testid="expiry-badge">{days < 0 ? `منتهي منذ ${-days} يوم` : days === 0 ? "ينتهي اليوم" : `متبقي ${days} يوم`}</span>;
};

const ColsEditor = ({ cols, setCols }) => (
  <div className="space-y-1.5 rounded-xl border border-dashed border-slate-300 p-2" data-testid="info-table-cols">
    {cols.map((c, i) => (
      <div key={c.key} className="flex gap-1.5">
        <Input value={c.label} onChange={(e) => setCols(cols.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="اسم العمود" className="h-8 text-xs" data-testid={`info-col-label-${i}`} />
        <select value={c.type} onChange={(e) => setCols(cols.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)))} className="h-8 rounded-md border border-slate-200 bg-white px-1 text-xs" data-testid={`info-col-type-${i}`}>
          {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button type="button" onClick={() => setCols(cols.filter((_, j) => j !== i))} className="px-1 text-slate-400 hover:text-red-500" data-testid={`info-col-del-${i}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    ))}
    <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => setCols([...cols, { key: uid(), label: "", type: "text" }])} data-testid="info-col-add"><Plus className="h-3.5 w-3.5" /> عمود</Button>
  </div>
);

export const InfoTablePage = ({ page, canEdit, onSave }) => {
  const [cols, setCols] = useState(page.columns || []);
  const [rows, setRows] = useState(page.rows || []);
  const [editCols, setEditCols] = useState(!(page.columns || []).length);
  const [busy, setBusy] = useState(false);
  const setCell = (ri, k, v) => setRows(rows.map((r, j) => (j === ri ? { ...r, cells: { ...r.cells, [k]: v } } : r)));
  const save = async () => {
    setBusy(true);
    try { await onSave({ ...page, columns: cols, rows }); setEditCols(false); toast.success("تم الحفظ"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const expKeys = cols.filter((c) => c.type === "expiry").map((c) => c.key);
  const soon = rows.filter((r) => expKeys.some((k) => r.cells?.[k] && (new Date(r.cells[k]) - Date.now()) / 86400000 <= 30)).length;
  return (
    <div className="space-y-3" data-testid={`info-table-${page.id}`}>
      {soon > 0 && <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="info-table-soon"><AlertTriangle className="h-4 w-4" /> {soon} سجل منتهٍ أو ينتهي خلال 30 يوماً</p>}
      {canEdit && editCols && <ColsEditor cols={cols} setCols={setCols} />}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-right text-xs">
          <thead className="bg-slate-50 text-slate-500"><tr>{cols.map((c) => <th key={c.key} className="whitespace-nowrap p-2">{c.label}</th>)}{canEdit && <th className="w-8" />}</tr></thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`info-row-${ri}`}>
                {cols.map((c) => (
                  <td key={c.key} className="p-1.5 align-top">
                    {canEdit ? <Input type={c.type === "expiry" || c.type === "date" ? "date" : c.type === "number" ? "number" : "text"} value={r.cells?.[c.key] || ""}
                      onChange={(e) => setCell(ri, c.key, e.target.value)} className="h-8 min-w-[110px] text-xs" data-testid={`info-cell-${ri}-${c.key}`} />
                      : <span>{r.cells?.[c.key] || "—"}</span>}
                    {c.type === "expiry" && <div className="mt-1"><ExpiryBadge date={r.cells?.[c.key]} /></div>}
                  </td>
                ))}
                {canEdit && <td className="p-1.5"><button type="button" onClick={() => setRows(rows.filter((_, j) => j !== ri))} className="text-slate-300 hover:text-red-500" data-testid={`info-row-del-${ri}`}><Trash2 className="h-4 w-4" /></button></td>}
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={cols.length + 1} className="p-6 text-center text-slate-400">لا توجد سجلات</td></tr>}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="gap-1" disabled={!cols.length} onClick={() => setRows([...rows, { id: uid(), cells: {} }])} data-testid="info-row-add"><Plus className="h-4 w-4" /> سجل</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditCols((x) => !x)} data-testid="info-cols-toggle">{editCols ? "إخفاء الأعمدة" : "تعديل الأعمدة"}</Button>
          <Button size="sm" className="mr-auto gap-1" disabled={busy} onClick={save} data-testid="info-table-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ</Button>
        </div>
      )}
    </div>
  );
};
